import axios from "axios";
import { PLATFORM, BASE, TOKEN_KEY, USER_KEY, CREDITS_EVENT, platformLogin } from "@/lib/platform";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
});

// Staff token and showroom customer session, for every API call. Also used
// by the few raw fetch() calls (multipart voice, audio blobs).
export function authHeaders() {
  const h = {};
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) h.Authorization = `Bearer ${token}`;
  try {
    const raw = localStorage.getItem("heda_customer_session");
    const s = raw ? JSON.parse(raw) : null;
    if (s?.session_id) h["X-Customer-Session"] = s.session_id;
  } catch {
    /* ignore */
  }
  return h;
}

api.interceptors.request.use((cfg) => {
  Object.assign(cfg.headers, authHeaders());
  return cfg;
});

// Platform credits refused an AI call (out of credits, not in plan): the
// platform's message goes to the notice in App. Works for axios errors and
// fetch() responses.
export async function noteCreditsRefusal(res) {
  const headers = res?.headers;
  const flag = headers?.get ? headers.get("X-Heda-Credits") : headers?.["x-heda-credits"];
  if (flag !== "refused") return;
  let detail = res.data?.detail;
  if (detail === undefined && res.clone) detail = (await res.clone().json().catch(() => ({}))).detail;
  window.dispatchEvent(new CustomEvent(CREDITS_EVENT, { detail: detail || "AI credits are not available." }));
}

api.interceptors.response.use(
  (r) => r,
  (err) => {
    noteCreditsRefusal(err?.response);
    // A staff token the server no longer accepts (expired, account disabled
    // or removed): drop it, and on admin pages go back to the login screen
    // instead of leaving every request failing until a reload.
    const cfg = err?.config || {};
    const sentToken = !!cfg.headers?.Authorization;
    if (err?.response?.status === 401 && sentToken && !/\/auth\/(login|platform-exchange)/.test(String(cfg.url || ""))) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      const path = window.location.pathname.slice(BASE.length);
      if (path.startsWith("/admin") && path !== "/admin/login") {
        if (PLATFORM) platformLogin();
        else window.location.assign(`${BASE}/admin/login`);
      }
    }
    return Promise.reject(err);
  },
);

export const fileUrl = (storagePath) =>
  storagePath ? `${API}/files/${storagePath}` : "";

// A URL the API handed out: "/api/..." is relative to the API, not the page.
export const backendUrl = (url) => (url && url.startsWith("/api/") ? `${BACKEND_URL}${url}` : url);

export default api;
